import type { Book, Language, Translation, TranslationState } from "../api";
import type { Messages } from "../i18n";

/**
 * What the studio shows about translating a book (requirement 0018). The
 * server decides everything — cost, status, where a run paused — and this
 * only words it.
 */

/** translatableBooks are the books a translation can start from: ones with a language and some text. */
export function translatableBooks(books: Book[]): Book[] {
  return books.filter((b) => !!b.language && (b.studio?.chapters ?? 0) > 0);
}

/** targetLanguages are the languages a book can be translated into: any but its own. */
export function targetLanguages(languages: Language[], source: Book | undefined): Language[] {
  if (!source) return [];
  return languages.filter((l) => l.code !== source.language);
}

/** holdsBook reports whether a run still owns its book's chapter list. */
export function holdsBook(state: TranslationState | null | undefined): boolean {
  return state?.status === "running" || state?.status === "paused";
}

/** headline is the banner's first line. */
export function headline(tr: Translation, t: Messages): string {
  switch (tr.status) {
    case "running":
      return t.translation.running(tr.chapters_done, tr.chapters_total);
    case "paused":
      return t.translation.paused;
    case "done":
      return t.translation.done(tr.source_title);
    default:
      return t.translation.cancelled;
  }
}

/** pauseReason says why a run stopped, in the interface's language. */
export function pauseReason(tr: Translation, t: Messages): string {
  const reason = t.translation.reasons[tr.reason ?? ""] ?? t.common.somethingWrong;
  return t.translation.reasonAt(tr.failed_chapter, reason);
}

/** summaryPart is the studio list's word on a book's run, or null when there is nothing to say. */
export function summaryPart(state: TranslationState | undefined, t: Messages): string | null {
  if (state?.status === "running")
    return t.translation.summaryRunning(state.chapters_done, state.chapters_total);
  if (state?.status === "paused") return t.translation.summaryPaused;
  return null;
}
